import React, { useState } from "react";
import {
  Alert,
  Chip,
  CircularProgress,
  FormControl,
  TextField,
} from "@mui/material";
import {
  AddOutlined,
  DeleteOutlined,
  DragIndicator,
  EditOutlined,
  InfoOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { api } from "../../../services/api";
import { sponsorVacio } from "../../../domain/evento/schema";
import { MAX_TAMANIO_ARCHIVO, MAX_TAMANIO_TEXTO } from "../../../utils/media";
import SeccionEvento from "./SeccionEvento";
import "./styles.css";

const SponsorsSection = ({ sponsors, onChange, errores, abierta, onToggle }) => {
  const [subiendo, setSubiendo] = useState(null);

  // el orden mostrado es el orden guardado: se renumera en cada cambio
  const conOrden = (lista) => lista.map((sponsor, i) => ({ ...sponsor, orden: i + 1 }));

  const actualizar = (lista) => onChange("sponsors", conOrden(lista));

  const agregar = () => actualizar([...sponsors, sponsorVacio(sponsors.length + 1)]);

  const cambiarNombre = (indice, valor) =>
    actualizar(sponsors.map((s, i) => (i === indice ? { ...s, nombre: valor } : s)));

  const subirLogo = async (indice, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.size > MAX_TAMANIO_ARCHIVO) {
      toast.error(`El archivo supera el límite de ${MAX_TAMANIO_TEXTO}.`);
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten archivos de imagen.");
      return;
    }

    setSubiendo(indice);
    const formData = new FormData();
    formData.append("files", file);

    try {
      const response = await api.media.uploadLocal(formData);
      if (response.status !== "success") {
        toast.error(response.message || "No se pudo subir el logo.");
        return;
      }

      const media = response.data.media[0];
      actualizar(
        sponsors.map((sponsor, i) =>
          i === indice
            ? {
                ...sponsor,
                url: media.url,
                type: media.type,
                format: media.format,
                public_id: media.public_id,
              }
            : sponsor
        )
      );
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("No se pudo subir el logo.");
    } finally {
      setSubiendo(null);
    }
  };

  const eliminar = async (indice) => {
    const sponsor = sponsors[indice];
    if (sponsor?.public_id) {
      try {
        await api.media.deleteLocal({ public_id: sponsor.public_id });
      } catch (error) {
        console.error("Error deleting sponsor image:", error);
      }
    }
    actualizar(sponsors.filter((_, i) => i !== indice));
  };

  const reordenar = (origen, destino) => {
    if (origen === destino) return;
    const siguiente = [...sponsors];
    const [movido] = siguiente.splice(origen, 1);
    siguiente.splice(destino, 0, movido);
    actualizar(siguiente);
  };

  return (
    <SeccionEvento
      id="sponsors"
      titulo="Sponsors del evento"
      tituloExpandido="Sponsors y patrocinadores"
      descripcion="Agregá a los patrocinadores de tu evento."
      tieneError={Boolean(errores.sponsors)}
      abierta={abierta}
      onToggle={onToggle}
    >
      <FormControl fullWidth>
        <p className="descrip-seccion-evento-expanded" style={{ fontSize: "16px", lineHeight: "24px" }}>
          ¿Contás con apoyo de marcas o empresas? Subí sus logos y mostralos en tu evento.
          <br />
          <span>Podés elegir el orden en que se mostrarán.</span>
        </p>

        <div className="info-input-img">
          <div className="d-flex gap-2 justify-content-center align-items-center w-100 flex-wrap">
            <Chip
              icon={<InfoOutlined sx={{ color: "#5656FF !important" }} />}
              size="small"
              label={`Tamaño máximo: ${MAX_TAMANIO_TEXTO}`}
              sx={{ backgroundColor: "#E6E6FF", color: "#5656FF" }}
            />
            <Chip
              icon={<InfoOutlined sx={{ color: "#5656FF !important" }} />}
              size="small"
              label="Formatos: JPEG, PNG"
              sx={{ backgroundColor: "#E6E6FF", color: "#5656FF" }}
            />
          </div>
        </div>

        <div className="sponsors-container">
          {sponsors.map((sponsor, indice) => (
            <div
              key={sponsor.id ?? `sponsor-${indice}`}
              className="sponsor-card"
              draggable
              onDragStart={(e) => e.dataTransfer.setData("text/plain", indice)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                reordenar(parseInt(e.dataTransfer.getData("text/plain"), 10), indice);
              }}
            >
              <div className="sponsor-card-header">
                <DragIndicator sx={{ color: "#666", cursor: "grab" }} />
                <span className="sponsor-order">#{sponsor.orden}</span>
                <button
                  type="button"
                  className="delete-sponsor-btn"
                  onClick={() => eliminar(indice)}
                  aria-label={`Eliminar sponsor ${indice + 1}`}
                >
                  <DeleteOutlined sx={{ color: "#ff4444" }} />
                </button>
              </div>

              <div className="sponsor-content">
                <TextField
                  label="Nombre"
                  value={sponsor.nombre || ""}
                  onChange={(e) => cambiarNombre(indice, e.target.value)}
                  size="small"
                  fullWidth
                  margin="normal"
                />

                <div className="sponsor-image-upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => subirLogo(indice, e)}
                    style={{ display: "none" }}
                    id={`sponsor-file-${sponsor.id ?? indice}`}
                  />

                  {sponsor.url ? (
                    <div className="sponsor-preview">
                      <img src={sponsor.url} alt={sponsor.nombre || "Sponsor"} />
                      <div className="image-overlay">
                        <label
                          htmlFor={`sponsor-file-${sponsor.id ?? indice}`}
                          className="edit-image-btn"
                        >
                          <EditOutlined sx={{ fontSize: "20px" }} />
                        </label>
                      </div>
                      {subiendo === indice && (
                        <div className="image-loading-overlay">
                          <CircularProgress size={30} />
                          <small>Subiendo...</small>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="upload-sponsor-container">
                      {subiendo === indice ? (
                        <div className="upload-loading">
                          <CircularProgress size={40} />
                          <p>Subiendo imagen...</p>
                        </div>
                      ) : (
                        <label
                          htmlFor={`sponsor-file-${sponsor.id ?? indice}`}
                          className="upload-sponsor-btn"
                        >
                          <UploadFileOutlined sx={{ fontSize: "32px" }} />
                          <span>Subir logo</span>
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          <button type="button" className="add-sponsor-btn" onClick={agregar}>
            <AddOutlined sx={{ color: "#3659E3" }} />
            <span>Agregar sponsor</span>
          </button>
        </div>

        {errores.sponsors && (
          <Alert severity="error" sx={{ marginTop: "10px" }}>
            {errores.sponsors}
          </Alert>
        )}
      </FormControl>
    </SeccionEvento>
  );
};

export default SponsorsSection;
